import { getIdToken, getSession, isMockMode } from './auth'
import * as mockApi from './mockApi'

const API_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '')

async function authFetch(path, options = {}) {
  const token = await getIdToken()
  if (!token) {
    const err = new Error('Not authenticated')
    err.status = 401
    throw err
  }

  const res = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
      ...(options.headers || {}),
    },
  })

  if (!res.ok) {
    const err = new Error(`API ${res.status}`)
    err.status = res.status
    try {
      err.body = await res.json()
      if (err.body?.message) err.message = err.body.message
    } catch {
      /* ignore */
    }
    throw err
  }

  if (res.status === 204) return null
  return res.json()
}

async function withSession(fn) {
  const session = await getSession()
  if (!session) {
    const err = new Error('Not authenticated')
    err.status = 401
    throw err
  }
  return fn(session)
}

export async function getMe() {
  if (isMockMode()) {
    return withSession((s) => mockApi.getMe(s))
  }
  return authFetch('/me')
}

export async function setMyCollege({ collegeId, collegeName, workshopDate } = {}) {
  if (isMockMode()) {
    return withSession((s) => mockApi.setMyCollege(s, { collegeId, collegeName, workshopDate }))
  }
  return authFetch('/me/college', {
    method: 'PUT',
    body: JSON.stringify({ collegeId, collegeName, workshopDate }),
  })
}

export async function requestCollege(collegeName) {
  if (isMockMode()) {
    return withSession((s) => mockApi.requestCollege(s, collegeName))
  }
  return authFetch('/me/college-request', {
    method: 'POST',
    body: JSON.stringify({ collegeName }),
  })
}

export async function checkAdmin() {
  if (isMockMode()) {
    return withSession((s) => mockApi.checkAdmin(s))
  }
  return authFetch('/auth/check-admin')
}

export async function listColleges({ q, all } = {}) {
  if (isMockMode()) {
    return withSession((s) => mockApi.listColleges(s, { q, all }))
  }
  const params = new URLSearchParams()
  if (q) params.set('q', q)
  if (all) params.set('all', '1')
  const qs = params.toString()
  return authFetch(`/colleges${qs ? `?${qs}` : ''}`)
}

export async function adminListColleges() {
  if (isMockMode()) {
    return withSession((s) => mockApi.adminListColleges(s))
  }
  return authFetch('/admin/colleges')
}

export async function adminListCollegeRequests() {
  if (isMockMode()) {
    return withSession((s) => mockApi.adminListCollegeRequests(s))
  }
  return authFetch('/admin/college-requests')
}

export async function adminCreateCollege(payload) {
  if (isMockMode()) {
    return withSession((s) => mockApi.adminCreateCollege(s, payload))
  }
  return authFetch('/admin/colleges', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export async function adminUpdateCollege(id, payload) {
  if (isMockMode()) {
    return withSession((s) => mockApi.adminUpdateCollege(s, id, payload))
  }
  return authFetch(`/admin/colleges/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  })
}

export async function adminDeleteCollege(id) {
  if (isMockMode()) {
    return withSession((s) => mockApi.adminDeleteCollege(s, id))
  }
  return authFetch(`/admin/colleges/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
}

export async function listUsers({ status } = {}) {
  if (isMockMode()) {
    return withSession((s) => mockApi.listUsers(s, { status }))
  }
  const q = status ? `?status=${encodeURIComponent(status)}` : ''
  return authFetch(`/admin/users${q}`)
}

export async function approveUser(sub, { sessionId, collegeId, workshopDate, collegeName } = {}) {
  if (isMockMode()) {
    return withSession((s) =>
      mockApi.approveUser(s, sub, { sessionId, collegeId, workshopDate, collegeName }),
    )
  }
  return authFetch(`/admin/users/${encodeURIComponent(sub)}/approve`, {
    method: 'POST',
    body: JSON.stringify({ sessionId, collegeId, workshopDate, collegeName }),
  })
}

export async function removeUser(sub) {
  if (isMockMode()) {
    return withSession((s) => mockApi.removeUser(s, sub))
  }
  return authFetch(`/admin/users/${encodeURIComponent(sub)}/remove`, { method: 'POST' })
}

// `choice` is a single choiceId (single-select polls) or an array of choiceIds
// (multi-select polls, poll.allowMultiple). The request body uses `choiceIds`
// for the array form and `choiceId` for the scalar form.
export async function vote(slideId, choice) {
  if (isMockMode()) {
    return withSession((s) => mockApi.vote(s, slideId, choice))
  }
  const body = Array.isArray(choice) ? { choiceIds: choice } : { choiceId: choice }
  return authFetch(`/polls/${encodeURIComponent(slideId)}/vote`, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

export async function getMyVote(slideId) {
  if (isMockMode()) {
    return withSession((s) => mockApi.getMyVote(s, slideId))
  }
  return authFetch(`/polls/${encodeURIComponent(slideId)}/me`)
}

export async function getPollResults(slideId, { collegeId, collegeName, workshopDate } = {}) {
  if (isMockMode()) {
    return withSession((s) =>
      mockApi.getPollResults(s, slideId, { collegeId, collegeName, workshopDate }),
    )
  }
  const params = new URLSearchParams()
  if (collegeId) params.set('collegeId', collegeId)
  if (collegeName) params.set('collegeName', collegeName)
  if (workshopDate) params.set('workshopDate', workshopDate)
  const q = params.toString() ? `?${params}` : ''
  return authFetch(`/polls/${encodeURIComponent(slideId)}/results${q}`)
}

export async function clearPollResults(slideId, { collegeId, collegeName, workshopDate } = {}) {
  if (isMockMode()) {
    return withSession((s) =>
      mockApi.clearPollResults(s, slideId, { collegeId, collegeName, workshopDate }),
    )
  }
  const params = new URLSearchParams()
  if (collegeId) params.set('collegeId', collegeId)
  if (collegeName) params.set('collegeName', collegeName)
  if (workshopDate) params.set('workshopDate', workshopDate)
  const q = params.toString() ? `?${params}` : ''
  return authFetch(`/polls/${encodeURIComponent(slideId)}/results${q}`, { method: 'DELETE' })
}

export async function getProgress() {
  if (isMockMode()) {
    return withSession((s) => mockApi.getProgress(s))
  }
  return authFetch('/progress')
}

export async function putProgress(lastSlideId) {
  if (isMockMode()) {
    return withSession((s) => mockApi.putProgress(s, lastSlideId))
  }
  return authFetch('/progress', {
    method: 'PUT',
    body: JSON.stringify({ lastSlideId }),
  })
}
